import { z } from "zod";
import { parseModelArrayTolerant } from "../../lib/parseModelArray";
import { procedureSessionEnvelopeSchema } from "./auditSession";
import { lifecycleStateSchema, testsStateSchema } from "./lifecycleState";
import { DryControlSchema } from "./control";
import { FyCycleOptionSchema } from "./fyCycleOption";
import { DryWorkStepSchema, WorkStepAttributeSchema } from "./workStep";
import { OptroLinkSchema } from "./optroLink";

export const testPhaseValues = [
  "not_defined",
  "test_0",
  "test_1",
  "test_2",
  "test_3",
] as const;

export type TestPhase = (typeof testPhaseValues)[number];

export const isTestPhase = (candidate: string): candidate is TestPhase =>
  testPhaseValues.some((phase) => phase === candidate);

export const TestPhases: ReadonlyArray<{ value: TestPhase; label: string }> = [
  { value: "not_defined", label: "Not Defined" },
  { value: "test_0", label: "Test 0" },
  { value: "test_1", label: "Test 1" },
  { value: "test_2", label: "Test 2" },
  { value: "test_3", label: "Test 3" },
];

// Control Session schema
export const ControlSessionSchema = z.object({
  id: z
    .string()
    .uuid()
    .default(() => crypto.randomUUID()),
  controlId: z.string(),
  userId: z.string(),
  workspaceId: z.string(),
  name: z.string(),
  wpTemplateId: z.string().nullable().optional(),
  status: z
    .enum(["in_progress", "in_review", "complete"])
    .nullable()
    .optional(),
  testPhase: z.enum(testPhaseValues).default("not_defined"),
  fyCycle: FyCycleOptionSchema.nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const ControlSessionWithUserSchema = ControlSessionSchema.extend({
  user: z.object({
    id: z.string(),
    email: z.string(),
    createdAt: z.string(),
  }),
});

export type ControlSessionWithUser = z.infer<
  typeof ControlSessionWithUserSchema
>;

// ============================================================================
// Phase 2 (megaquery decomposition) — envelope + status schemas
// Backend serializes via Pydantic `alias_generator=to_camel`, so wire format
// is camelCase. Field names below intentionally match the BE JSON.
// ============================================================================

// Envelope-level control mirrors the BE `DryControl` shape — the Phase-1
// envelope endpoint serializes the dry record only. Consumers that need the
// heavy `attributes`/`keyReportItems` collections must fetch them via
// `useControl(controlId)` (`GET /controls/v2/{controlId}` returns the
// hydrated `ControlWithProcedure`).
export const controlEnvelopeSchema = DryControlSchema;

// This envelope is shared between control sessions
// (`GET /controls/v2/control-session/{id}`) and work step sessions
// (`GET /work-steps/work-step-session/{id}`). A work-step-headed envelope has
// `workStepId`/`workStep` instead of `controlId`/`control`, and omits the
// `testPhase`/`fyCycleId`/`fyCycle` keys entirely — so the control-only fields
// default to null (or "not_defined") when absent, and vice versa. Consumers
// must guard on presence; a control session still always carries
// `controlId`/`control`/`testPhase`.
export const controlSessionEnvelopeSchema = z.object({
  id: z.string().uuid(),
  controlId: z.string().uuid().nullable().default(null),
  workStepId: z.string().uuid().nullable().default(null),
  // Work step sessions may be headless (`user_id: str | None` on the BE).
  userId: z.string().nullable(),
  workspaceId: z.string(),
  name: z.string(),
  status: z.enum(["in_progress", "in_review", "complete"]),
  testPhase: z.enum(testPhaseValues).optional().default("not_defined"),
  fyCycleId: z.string().uuid().nullable().default(null),
  wpTemplateId: z.string().uuid().nullable(),
  longTimeoutsEnabled: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  control: controlEnvelopeSchema.nullable().default(null),
  // Full work step on the wire (incl. attributes/procedures); the envelope
  // needs the header fields plus `attributes` (the Workpaper attribute views
  // read them from here for a work-step session), so only procedures drop.
  workStep: DryWorkStepSchema.extend({
    attributes: z.array(WorkStepAttributeSchema).default([]),
  })
    .nullable()
    .default(null),
  fyCycle: FyCycleOptionSchema.nullable().default(null),
  procedureSessions: z.array(procedureSessionEnvelopeSchema),
  // Gates the auditor-trail (Evidence Walk V3) UI — the three-pane fold-out and
  // the "Test Review" tab. Defaults off so a missing/older payload hides it.
  auditorTrailEnabled: z.boolean().optional().default(false),
  // The auditor-trail flag is on but this session's results predate it (results
  // exist, no trail persisted) — shows the "regenerate the workpaper to see the
  // new evidence review" banner. Never true alongside auditorTrailEnabled.
  auditorTrailPending: z.boolean().optional().default(false),
  // The Optro record linked to this session, resolved server-side. Null
  // means the session is not linked to Optro, or the lookup failed (below).
  // The pill is decoration: a link this build can't read hides it rather than
  // failing the whole envelope, which would send the user out of the session.
  optroLink: OptroLinkSchema.nullable().default(null).catch(null),
  // True when the backend could not resolve the Optro link; `optroLink` is
  // then null whether or not the session is linked.
  optroLinkError: z.boolean().default(false),
  // When this session was last sent to Optro, or null if it never has been.
  // Backs the Resend label and "Last sent" tooltip so they survive a reload.
  optroLastSentAt: z.string().nullable().default(null),
});

export type ControlSessionEnvelope = z.infer<
  typeof controlSessionEnvelopeSchema
>;

// Lifecycle fields parse through the shared tolerant schemas (see
// `lifecycleState.ts`): an unrecognized value degrades to "unknown", which
// `hasActiveLifecycleState` below deliberately treats as settled.
export const procedureSessionLifecycleSchema = z.object({
  id: z.string(),
  state: lifecycleStateSchema,
  documentsState: lifecycleStateSchema,
  testsState: testsStateSchema,
  sourceRowsState: lifecycleStateSchema,
  workPaperState: lifecycleStateSchema,
});

export type ProcedureSessionLifecycle = z.infer<
  typeof procedureSessionLifecycleSchema
>;

export const controlSessionStatusResponseSchema = z.object({
  procedureSessions: z.array(procedureSessionLifecycleSchema),
});

export type ControlSessionStatusResponse = z.infer<
  typeof controlSessionStatusResponseSchema
>;

// Lifecycle values that mean the backend is still working. Everything else
// ("ready" AND the "unknown" degraded sentinel) is treated as settled — the
// pollers key off this, so an unknown value stops polling instead of
// spinning forever. Widened to `readonly string[]` so `.includes` accepts
// any lifecycle field's union.
const activeLifecycleStates: readonly string[] = [
  "pending",
  "processing",
  "retrying",
];

/** True while at least one lifecycle field of `ps` is still in flight —
 * the shared poll-continuation predicate for `useControlSessionStatus` and
 * `useSessionRunsPolling`. */
export const hasActiveLifecycleState = (
  ps: ProcedureSessionLifecycle,
): boolean =>
  [
    ps.state,
    ps.documentsState,
    ps.testsState,
    ps.sourceRowsState,
    ps.workPaperState,
  ].some((state) => activeLifecycleStates.includes(state));

const statusResponseEnvelopeSchema = z.object({
  procedureSessions: z.array(z.unknown()),
});

/**
 * Tolerant parse for the status endpoint: the envelope must be shaped right
 * (a real transport/shape failure should still throw into the fetcher's
 * error path), but each procedure-session row degrades independently — a
 * malformed row is dropped and unknown enum values coerce to "unknown"
 * (see the schema `.catch`es above) instead of poisoning the whole poll.
 */
export function parseControlSessionStatusResponse(
  response: unknown,
): ControlSessionStatusResponse {
  const envelope = statusResponseEnvelopeSchema.parse(response);
  return {
    procedureSessions: parseModelArrayTolerant(
      procedureSessionLifecycleSchema,
      envelope.procedureSessions,
      "procedure-session lifecycle",
    ),
  };
}
